// src/lib/helpers/index.js
import { browser } from '$app/environment';
import { checkStock } from '$lib/firestore/checkStock';
import { onMount } from 'svelte';
import { toasts, ToastContainer, FlatToast } from 'svelte-toasts';
import { cartContents, createOrUpdateCart, theCart } from '$stores/cart';
import {
	differenceInCalendarDays,
	parseISO,
	isValid,
	addDays,
	addWeeks,
	setDay,
	getDay
} from 'date-fns';

import { auth } from '$lib/auth';
// import { ga } from '@beyonk/svelte-google-analytics'

import SvelteMarkdown from 'svelte-markdown';
import posthog from 'posthog-js';
import { get } from 'svelte/store';
import { referralCodeStore } from '$stores/referrals';
import { page } from '$app/stores';
import { goto } from '$app/navigation';
import { logError } from './errors';
// import { getFirestoreDb } from '$lib/firebase.admin';

const ticketCap = 1000;

export function truncateString(str, num) {
	if (!str) return;
	if (str.length > num) {
		return str.slice(0, num) + '...';
	} else {
		return str;
	}
}

export function showToast(title, description) {
	toasts.clearAll();

	const toast = toasts.add({
		title,
		description,
		duration: 5000, // 0 or negative to avoid auto-remove
		placement: 'bottom-center',
		showProgress: true,
		type: 'success',
		theme: 'dark',
		onClick: () => {},
		onRemove: () => {}
		// component: BootstrapToast, // allows to override toast component/template per toast
	});
}

// Remove item from cart
export function removeFromCart(id) {
	try {
		cartContents.update((n) => {
			const itemExists = n.filter((el) => {
				return el.item.id == id;
			});
			if (itemExists.length) {
				const filteredArr = n.filter((el) => {
					return el.item.id !== id;
				});

				showToast('Item removed', '');

				return filteredArr;
			} else {
				showToast('Error', 'Item does not exist');
				return [...n];
			}
		});

		createOrUpdateCart();
	} catch (error) {
		console.error('removeFromCart error: ', error);
	}
}

export function showStockChangeToast(title, description) {
	const toast = toasts.add({
		title,
		description,
		duration: 5000, // 0 or negative to avoid auto-remove
		placement: 'bottom-center',
		showProgress: true,
		type: 'error',
		theme: 'dark',
		onClick: () => {},
		onRemove: () => {}
		// component: BootstrapToast, // allows to override toast component/template per toast
	});
}

export function showErrorToast(title, description) {
	const toast = toasts.add({
		title,
		description,
		duration: 5000, // 0 or negative to avoid auto-remove
		placement: 'bottom-center',
		showProgress: true,
		type: 'error',
		theme: 'dark',
		onClick: () => {},
		onRemove: () => {}
		// component: BootstrapToast, // allows to override toast component/template per toast
	});
}

export function buildValidator(validators) {
	return function validate(value, dirty) {
		if (!validators || validators.length === 0) {
			return { dirty, valid: true };
		}

		const failing = validators.find((v) => v(value) !== true);

		return {
			dirty,
			valid: !failing,
			message: failing && failing(value)
		};
	};
}

export function validateEmail(email) {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		);
}

export function updateQty(pid, newQty) {
	try {
		const quantity = parseInt(newQty, 10);

		// Validate newQty
		if (isNaN(quantity) || quantity <= 0) {
			showErrorToast('Invalid Quantity', 'Please enter a valid quantity.');
			return;
		}

		cartContents.update((currentCart) => {
			return currentCart.map((cartItem) => {
				if (cartItem.id === pid) {
					// Check stock availability
					if (cartItem.item.stock < quantity) {
						showErrorToast('Sorry', `There's only ${cartItem.item.stock} tickets left`);
						return cartItem; // No change
					}

					// Calculate the new total cost based on the new quantity
					const newTotalCost = parseFloat(cartItem.item.price) * quantity;

					// Handle ticket cap
					if (quantity > ticketCap) {
						showToast('Notice', `Max ${ticketCap} tickets allowed. Quantity adjusted.`);
						return {
							...cartItem,
							quantity: ticketCap,
							cost: parseFloat(cartItem.item.price) * ticketCap,
							purchasePrice: (parseFloat(cartItem.item.price) * ticketCap).toFixed(2)
						};
					}

					// Update the item with the new quantity and cost
					showToast(
						'Success',
						`Quantity updated to ${quantity} ${quantity === 1 ? 'ticket' : 'tickets'}`
					);
					return {
						...cartItem,
						quantity: quantity,
						cost: newTotalCost,
						purchasePrice: newTotalCost.toFixed(2)
					};
				}
				return cartItem;
			});
		});

		createOrUpdateCart();
	} catch (error) {
		console.error('updateQty error: ', error);
		showErrorToast('Cart Error', 'An issue occurred while updating the cart');
	}
}

export const mapSnapshot = (snap) => {
	let productArr = [];

	snap.forEach((doc) => {
		// doc.data() is never undefined for query doc snapshots
		// console.log(doc.id, " => ", doc.data());
		productArr.push({
			id: doc.id,
			...doc.data()
		});
	});

	return productArr;
};

export function addToCart({ item, qty, cost, pid }) {
	try {
		// Ensure the item has a valid ID by using item.id or pid
		const thisId = pid || item?.id;
		console.log({ thisId }); // Log thisId to confirm it's being set correctly

		if (!thisId) {
			console.error('Item must have an ID or a valid PID to add to cart');
			showErrorToast('Invalid Item', 'Unable to add to cart without a valid item ID');
			return;
		}

               const discountAmount = get(theCart)?.ticketBundles?.bundleDiscountAmount || 0;
               const value = parseFloat(cost);

               cartContents.update((currentCart) => {
			// Find the item in the cart by thisId
			const existingItemIndex = currentCart.findIndex((cartItem) => cartItem.id === thisId);

			if (existingItemIndex !== -1) {
				// Item already in cart, update quantity and cost
				const existingItem = { ...currentCart[existingItemIndex] };

				// Check if there's enough stock
				if (item.stock < qty + existingItem.quantity) {
					showErrorToast('Sorry', `There's only ${item.stock} tickets left`);
					return currentCart; // Return original cart to avoid breaking the flow
				}

				// Calculate the new quantity, respecting the ticket cap
				const newQty = existingItem.quantity + qty;
				if (newQty > ticketCap) {
					const maxQty = ticketCap - existingItem.quantity;
					showToast('Sorry', `Max ${ticketCap} tickets, but we've added ${maxQty} more`);
					existingItem.quantity = ticketCap;
				} else {
					existingItem.quantity = newQty;
				}

				// Update the total cost and purchase price
				const additionalCost = parseFloat(cost); // 'cost' is total cost for the added quantity
				existingItem.cost += additionalCost;
				existingItem.purchasePrice = existingItem.cost.toFixed(2);

				// Show a success toast
				showToast('Success', `Added ${qty} ${qty === 1 ? 'ticket' : 'tickets'}`);

				// Replace the item in the cart and return updated cart
				return [
					...currentCart.slice(0, existingItemIndex),
					existingItem,
					...currentCart.slice(existingItemIndex + 1)
				];
			} else {
				// Item not in cart, add new item
				if (item.stock < qty) {
					showErrorToast('Sorry', `There's only ${item.stock} tickets left`);
					return currentCart; // Return original cart to avoid breaking the flow
				}

				const totalCost = parseFloat(cost); // Total cost based on quantity

				const newItem = {
					item,
					name: item.name,
					quantity: qty,
					cost: totalCost, // Total cost for the quantity
					id: thisId,
					purchasePrice: totalCost.toFixed(2), // Total cost as a string with two decimals
					discount: discountAmount
				};

				// Show a success toast
				showToast('Success', `${qty} ${qty === 1 ? 'ticket' : 'tickets'} added, good luck!`);

				// Add the new item to the cart and return updated cart
				return [...currentCart, newItem];
			}
               });

               if (typeof window !== 'undefined' && window.rdt) {
                       window.rdt('track', 'AddToCart', {
                               content_id: thisId,
                               value,
                               currency: 'GBP'
                       });
               }

               createOrUpdateCart();
	} catch (error) {
		console.error('Add to cart error: ', error);
		showErrorToast('Cart Error', 'An issue occurred while adding to cart');
	}
}

export async function checkStockLevel(itm) {
	// Validate the item object and ensure it has an ID
	const itemID = itm?.id || itm.item?.id;

	if (!itemID) {
		console.warn('Invalid item passed to checkStockLevel:', itm);
		return;
	}

	try {
		const requestedQty = itm.qty; // Requested quantity in the cart

		// Fetch the current stock from Firestore or another source
		const availableStock = await checkStock(itemID);

		// Handle the case when stock check fails or stock is undefined/null
		if (availableStock === null || availableStock === undefined) {
			console.warn(`Stock check failed for item ID: ${itemID}`);
			return;
		}

		// If no stock is available, remove the item from the cart
		if (availableStock === 0) {
			showStockChangeToast('Ticket Stock Check', `Sorry, we've had to remove ${itm.item.name}`);
			setTimeout(() => {
				removeFromCart(itemID);
			}, 2000);
			return;
		}

		// If requested quantity exceeds available stock, adjust the quantity in the cart
		if (requestedQty > availableStock) {
			showStockChangeToast(
				'Ticket Stock Check',
				`Sorry, we've had to reduce your tickets for ${itm.item.name}`
			);
			itm.qty = availableStock; // Adjust the quantity
		}

		// Return the available stock level
		return availableStock;
	} catch (error) {
		console.error('Error in checkStockLevel:', error);
	}
}

export function poll(fn, ms) {
	onMount(() => {
		const interval = setInterval(fn, ms);
		fn();

		return () => clearInterval(interval);
	});
}

export function randomInt(min, max) {
	// min and max included
	return Math.floor(Math.random() * (max - min + 1) + min);
}

export function convertDate(dateString) {
	if (!dateString) return;
	var p = dateString.split(/\D/g);
	return [p[2], p[1], p[0]].join('/');
}

export function timeRemaining(endDate) {
	if (!endDate || endDate !== typeof String || endDate == 'undefined') {
		return;
	}
	// console.log({endDate})
	if (Object.prototype.toString.call(endDate) !== '[object Date]') {
		return;
	}

	var isoDate = new Date(endDate).toISOString();

	var utc = new Date().toJSON().slice(0, 10).replace(/-/g, '/');

	const _MS_PER_DAY = 1000 * 60 * 60 * 24;
	// a and b are javascript Date objects
	function dateDiffInDays(a, b) {
		// Discard the time and time-zone information.
		const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
		const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

		return Math.floor((utc2 - utc1) / _MS_PER_DAY);
	}

	let daysDiff = dateDiffInDays(new Date(utc), new Date());

	return daysDiff;
}

export function checkProperties(obj) {
	const vals = Object.values(obj);
	if (!vals?.includes(undefined)) {
		return true;
	} else {
		return false;
	}
}

export function percentage(partialValue, totalValue) {
	return (100 * partialValue) / totalValue;
}

export function percentageOff(num, per) {
	const t = Number(num);
	const d = Number(per);
	return t > 0 ? (d / t) * 100 : 0;
}

export function triggerForegroundPush(data) {
	return showToast(data.title, data.body);
}

/**
 * Calculates the number of days remaining until the given end date.
 * @param {string|Date} endDate - The end date.
 * @returns {number} - The number of days remaining.
 */
export function daysRemaining(endDate) {
	try {
		if (!endDate) {
			return Infinity;
		}

		let parsedDate;
		if (typeof endDate === 'string') {
			parsedDate = parseISO(endDate);
		} else if (endDate instanceof Date) {
			parsedDate = endDate;
		} else {
			parsedDate = parseISO(String(endDate));
		}

		if (!isValid(parsedDate)) {
			return Infinity;
		}

		const today = new Date();
		const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
		return differenceInCalendarDays(parsedDate, startOfToday);
	} catch (error) {
		logError(error, 'daysRemaining');
		return Infinity;
	}
}

/**
 * Calculates the next Monday from the current date.
 * @returns {Date} - The next Monday date.
 */
export function untilLiveDraw() {
	const dayINeed = 1; // 1 corresponds to Monday (Sunday = 0, Monday = 1, ..., Saturday = 6)
	const today = new Date();
	const currentDay = getDay(today);

	let nextMonday;
	if (currentDay <= dayINeed) {
		// If today is before or on Monday, get this week's Monday
		nextMonday = setDay(today, dayINeed);
	} else {
		// Otherwise, get next week's Monday
		nextMonday = addWeeks(setDay(today, dayINeed), 1);
	}
	return nextMonday;
}

export function handlePromiseRejection(event) {
	console.error('Unhandled promise rejection:', event.reason);
	console.log('Rejected promise:', event.promise);
	logError(event.reason, 'unhandled_promise_rejection');
}

/**
 * Calculates the next Thursday from the current date.
 * @returns {Date} - The next Thursday date.
 */
export function untilThursdayDraw() {
	const dayINeed = 4; // 4 corresponds to Thursday (Sunday = 0, Monday = 1, ..., Saturday = 6)
	const today = new Date();
	const currentDay = getDay(today);

	let nextThursday;
	if (currentDay <= dayINeed) {
		// If today is before or on Thursday, get this week's Thursday
		nextThursday = setDay(today, dayINeed);
	} else {
		// Otherwise, get next week's Thursday
		nextThursday = addWeeks(setDay(today, dayINeed), 1);
	}
	return nextThursday;
}

export function sendGaEvent(eventTag, dataObject) {
	try {
		// ga.addEvent(eventTag, dataObject)
	} catch (error) {
		console.log('GA event error: ' + error);
	}
}

export async function sendEmailLink(email) {
	try {
		const res = await auth.emailLink(email);

		if (res === 200) {
			showToast(`Email link sent to ${email}`, 'Please check your inbox');
			return;
		}
		if (res === 500) {
			showToast('Error sending email link', 'Please try again');
		}
	} catch (error) {
		console.log('error send email link');
	}
}

// GET COOKIE
export function getCookie(name) {
	if (!browser) return;
	let cookieName = name + '=';
	let decodedCookie = decodeURIComponent(document.cookie);
	let ca = decodedCookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) == ' ') {
			c = c.substring(1);
		}
		if (c.indexOf(cookieName) == 0) {
			return c.substring(cookieName.length, c.length);
		}
	}
	return '';
}
// SET COOKIE
export function setCookie(name, value, exdays, secure) {
	if (!browser) return;
	const d = new Date();
	d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
	let expires = 'expires=' + d.toUTCString();
	if (secure) {
		document.cookie = name + '=' + value + ';' + expires + ';path=/; secure';
	} else {
		document.cookie = name + '=' + value + ';' + expires + ';path=/';
	}
}
// DELETE COOKIE
export function deleteCookie(name) {
	if (!browser) return;
	document.cookie = name + '=; Max-Age=-99999999;';
}

export function myRandomInts(existingNumberArray, quantity, max) {
	let set = new Set();
	if (existingNumberArray.length > 0) {
		for (let i = 0; i < existingNumberArray.length; i++) {
			set.add(existingNumberArray[i]);
		}
		// set = existingNumberArray
	}
	let numbersToPick = quantity;
	while (numbersToPick > 0) {
		set.add(Math.floor(Math.random() * max) + 1);

		numbersToPick--;
	}

	let arrayFrom = Array.from(set).filter((n) => {
		if (!existingNumberArray?.includes(n)) return n;
	});
	// console.log({arrayFrom})
	return arrayFrom;
}

export function initWebSocket(topicId) {
	// console.log('init web socket', topicId)
	const socket = new WebSocket(`wss://ntfy.sh/${topicId ? topicId : 'a4W2cHgS3TjjQg3Q'}/ws`);
	socket.addEventListener('message', function (event) {
		const ob = JSON.parse(event.data);

		if (ob.event == 'message') {
			let title = ob.title ? ob.title : ob.message;
			let bod = ob.message;
			// if (ob.content_type && ob.content_type == "text/markdown") {

			// }

			new Notification(ob.title ? ob.title : ob.message, {
				body: ob.message,
				icon: 'https://www.hotdraw.co.uk/hd-full-trans.png'
			});
		}
	});
}

export function notifyMe() {
	if (!browser) return;

	if (!('Notification' in window)) {
		// Check if the browser supports notifications
		alert('This browser does not support desktop notification');
	} else if (Notification.permission === 'granted') {
		// Check whether notification permissions have already been granted;
		// if so, create a notification
		// const notification = new Notification("Hi there!");
		// …
		initWebSocket();
	} else if (Notification.permission !== 'denied') {
		// We need to ask the user for permission
		Notification.requestPermission().then((permission) => {
			// If the user accepts, let's create a notification
			if (permission === 'granted') {
				// const notification = new Notification("Hi there!");

				initWebSocket();
				// …
			}
		});
	}

	// At last, if the user has denied notifications, and you
	// want to be respectful there is no need to bother them anymore.
}

export async function getIntent(amount) {
	console.log('Helper: getIntent: ', amount);
	let intentAmount = amount;
	const response = await fetch('/api/checkout/intent', {
		method: 'POST',
		body: JSON.stringify({
			amount: intentAmount,
			customerID: get(auth)?.user?.uid
		}),
		headers: {
			Accept: 'application/json, text/plain, */*',
			'content-type': 'application/json'
		}
	});

	const loadedResponse = await response.json();

	return loadedResponse?.intent;
}

export async function addPointsToAccount(rp) {
	// Fire on checkout sucess.
	try {
		auth.subscribe((v) => {
			let authId = v.user.uid;
			// addPoints(authId, rp)
			// 	.then((res) => {
			// 		console.log('addPoints to account result = ', res);
			// 		posthog.capture('payment_add_points_to_account', {
			// 			points: rp
			// 		});
			// 	})
			// 	.catch((err) => {
			// 		console.log('add points to account error, ', err);
			// 		posthog.capture('payment_add_points_to_account_error', {
			// 			error: err
			// 		});
			// 	});
		});
	} catch (error) {
		console.log('add points to account error, ', {
			error: error,
			rp: rp
			// uid: v.user.uid
		});
		posthog.capture('payment_add_points_to_account_error', {
			error: error,
			rp: rp
			// uid: v.user.uid
		});
	}
}

export function debounce(func, delay) {
	let timeoutId;
	return function (...args) {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			func.apply(this, args);
		}, delay);
	};
}

/**
 * Sanitizes user data to prevent injection and formatting issues.
 *
 * @param {String} firstName
 * @param {String} lastName
 * @param {String} email
 * @param {String} phone
 * @returns {Object} - Sanitized user data.
 */
export function sanitizeUserData(firstName, lastName, email, phone) {
	// Implement sanitization as needed, e.g., trimming, escaping special characters
	return {
		firstName: firstName ? firstName.trim() : '',
		lastName: lastName ? lastName.trim() : '',
		email: email ? email.trim().toLowerCase() : '',
		phone: phone ? phone.trim() : ''
	};
}

/**
 * Normalizes the cart field to always return an array of unique items.
 *
 * @param {Object|Array} cart - The cart field from the order document.
 * @returns {Array} - An array of unique cart items.
 */
export function normalizeCart(cart) {
	let items = [];

	if (Array.isArray(cart)) {
		items = cart;
	} else if (cart && typeof cart === 'object') {
		if (Array.isArray(cart.contents)) {
			items = cart.contents;
		} else {
			items = Object.values(cart);
		}
	}

	// Deduplicate items based on the 'id' field
	const uniqueItemsMap = new Map();
	items.forEach((item) => {
		if (item.id && !uniqueItemsMap.has(item.id)) {
			uniqueItemsMap.set(item.id, item);
		}
	});

	return Array.from(uniqueItemsMap.values());
}

export function injectReferralCode() {
	if (!browser) return;
	const url = new URL(window.location.href);
	// If a referral parameter exists in the URL, store it in a cookie
	if (url.searchParams.has('ref')) {
		const code = url.searchParams.get('ref');
		setCookie('referralCode', code, 7, true);
		// Optionally, update the URL without the referral parameter if desired:
		// url.searchParams.delete('ref');
		// goto(url.toString(), { replaceState: true });
	}
	// If no referral parameter is in the URL but a cookie exists, do nothing (the cookie will be read on signup)
}

export async function injectReferrerCode() {
	const usersReferralCode = await get(referralCodeStore);
	if (!usersReferralCode) return;
	const url = new URL(get(page).url);

	if (url.searchParams.has('ref')) return;

	// If no `ref` param, inject it
	if (!url.searchParams.has('ref')) {
		url.searchParams.set('ref', usersReferralCode);
		url.searchParams.set('utm_source', 'referral');
		url.searchParams.set('utm_medium', 'share');
		url.searchParams.set('utm_campaign', 'user_referral');

		// Use goto() with replaceState so there’s no extra history entry
		goto(url.toString(), { replaceState: true });
	}
}
